/**
 * Locations Selectors
 */

import { createSelector } from '@spcc/core'
import type { LocationsState } from './locations.slice'
import { LOCATION_TYPE_CODES } from '../domain/constants'

interface RootState {
  locations: LocationsState
}

// Base selectors
export const selectLocationsState = (state: RootState) => state.locations
export const selectCurrentLocation = (state: RootState) => state.locations.currentLocation
export const selectCurrentLocationId = (state: RootState) => state.locations.currentLocationId
export const selectDerivedFactoryId = (state: RootState) => state.locations.derivedFactoryId
export const selectLocationsList = (state: RootState) => state.locations.locationsList
export const selectLocationsLoading = (state: RootState) => state.locations.loading
export const selectLocationsError = (state: RootState) => state.locations.error

/**
 * Get location context for X-Location-Id header injection
 */
export const selectLocationContext = (state: RootState): string | null => {
  return state.locations.currentLocationId
}

/**
 * Get factory context derived from the selected location.
 * Used for X-Factory-Id header — returns null if location has no factory.
 */
export const selectLocationDerivedFactoryContext = (state: RootState): string | null => {
  return state.locations.derivedFactoryId
}

/**
 * Check if a location is currently selected
 */
export const selectHasLocationSelected = createSelector(
  [selectCurrentLocation],
  (location) => location !== null
)

/**
 * Display name for the selected location
 */
export const selectLocationDisplayName = createSelector(
  [selectCurrentLocation],
  (location) => {
    if (!location) return null

    const isFactoryLocation =
      location.locationType?.code === LOCATION_TYPE_CODES.FACTORY_LOCATION

    if (isFactoryLocation && location.factoryName) {
      return location.factoryName
    }

    return location.name
  }
)
